import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, Globe, TrendingUp, Cloud, Activity, Sparkles, Clock, 
  BarChart3, Grid3X3, History, Sun, MessageSquare, Newspaper,
  HelpCircle, Info
} from "lucide-react";

const PANEL_INFO = [
  {
    id: "main-mood",
    icon: Globe,
    title: "Global Mood",
    description: "The world's current emotional state, represented by a single adjective and color. Click the mood word for detailed analysis.",
    color: "#ff6b6b"
  },
  {
    id: "history",
    icon: TrendingUp,
    title: "History",
    description: "Mood trend over the past week. Shows how global sentiment has evolved. Click to see detailed visualization.",
    color: "#4ecdc4"
  },
  {
    id: "regional",
    icon: Globe,
    title: "Regional Breakdown",
    description: "Individual mood analysis for each region: North America, Europe, Asia, South America, Africa, Oceania, and Middle East.",
    color: "#45b7d1"
  },
  {
    id: "key-topics",
    icon: Cloud,
    title: "Key Topics",
    description: "Word cloud showing the most discussed topics driving global mood. Click any topic to see related articles and events.",
    color: "#96ceb4"
  },
  {
    id: "event-impact",
    icon: Activity,
    title: "Event Impact",
    description: "Major positive and negative events affecting global sentiment, with their relative impact on overall mood.",
    color: "#ffeaa7"
  },
  {
    id: "ai-prediction",
    icon: Sparkles,
    title: "AI Prediction",
    description: "Machine learning forecast of where the mood is heading, based on trending patterns and event analysis.",
    color: "#a29bfe"
  },
  {
    id: "forecast",
    icon: Clock,
    title: "24H Forecast",
    description: "Hour-by-hour mood prediction for the next 24 hours, helping you anticipate sentiment shifts.",
    color: "#74b9ff"
  },
  {
    id: "stock-market",
    icon: BarChart3,
    title: "Mood vs Markets",
    description: "Correlation between global mood and major stock indices (S&P 500, NASDAQ, Dow Jones). Shows how sentiment and markets move together.",
    color: "#00b894"
  },
  {
    id: "mood-meter",
    icon: Grid3X3,
    title: "Mood Meter",
    description: "A 10x10 matrix of 100 emotions organized by energy (high/low) and positivity (positive/negative). Shows exactly where the current mood sits.",
    color: "#fd79a8"
  },
  {
    id: "time-travel",
    icon: History,
    title: "Time Travel",
    description: "Browse historical moods by date. See what the world was feeling on any past day with full details.",
    color: "#e17055"
  },
  {
    id: "weather",
    icon: Sun,
    title: "Weather Influence",
    description: "How global weather patterns contribute to mood. Sunny skies often correlate with optimism, while storms may increase anxiety.",
    color: "#fdcb6e"
  },
  {
    id: "social",
    icon: MessageSquare,
    title: "Social Sentiment",
    description: "Real-time sentiment from Reddit, Hacker News, Mastodon, Google Trends, and Twitter/X. Shows what people are actually saying.",
    color: "#00cec9"
  },
  {
    id: "news-ticker",
    icon: Newspaper,
    title: "Live Sources",
    description: "Real-time feed showing which news sources are being analyzed. Over 100 sources from 7 regions worldwide.",
    color: "#636e72"
  }
];

export const HelpAboutModal = ({ isOpen, onClose, moodColor = "#0a84ff" }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
        data-testid="help-about-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="apple-card w-full max-w-2xl max-h-[85vh] overflow-hidden modal-glow"
          style={{
            '--modal-glow-color': `${moodColor || 'rgba(255,255,255,0.15)'}`,
            '--modal-glow-color-faded': `${moodColor}40`,
            '--modal-glow-color-dim': `${moodColor}20`,
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="help-about-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--apple-separator)' }}>
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${moodColor}20` }}
              >
                <HelpCircle size={22} style={{ color: moodColor }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--apple-text-primary)' }}>
                  About Mood of the World
                </h2>
                <p className="text-xs" style={{ color: 'var(--apple-text-tertiary)' }}>
                  Understanding each panel
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="help-modal-close"
            >
              <X size={20} style={{ color: 'var(--apple-text-secondary)' }} />
            </button>
          </div>

          {/* Introduction */}
          <div className="p-5 border-b" style={{ borderColor: 'var(--apple-separator)', backgroundColor: 'var(--apple-bg-secondary)' }}>
            <p className="text-sm" style={{ color: 'var(--apple-text-secondary)' }}>
              <strong>Mood of the World</strong> analyzes <strong>100+ global news sources</strong> every 5 minutes using AI to determine the world's emotional state. 
              Multiple LLMs (GPT-5.2, Claude, Gemini) analyze headlines to produce a nuanced mood, represented by a single adjective and color.
            </p>
          </div>

          {/* Panel List */}
          <div className="overflow-y-auto p-5" style={{ maxHeight: 'calc(85vh - 200px)' }}>
            <div className="space-y-3">
              {PANEL_INFO.map((panel) => {
                const IconComponent = panel.icon;
                return (
                  <div 
                    key={panel.id}
                    className="flex gap-4 p-4 rounded-xl transition-colors"
                    style={{ backgroundColor: 'var(--apple-bg-secondary)' }}
                    data-testid={`help-panel-${panel.id}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${panel.color}20` }}
                    >
                      <IconComponent size={20} style={{ color: panel.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm mb-1" style={{ color: 'var(--apple-text-primary)' }}>
                        {panel.title}
                      </h3>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--apple-text-secondary)' }}>
                        {panel.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Data Sources */}
            <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: 'var(--apple-bg-tertiary)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Info size={16} style={{ color: 'var(--apple-text-tertiary)' }} />
                <span className="text-xs font-medium" style={{ color: 'var(--apple-text-secondary)' }}>
                  DATA SOURCES
                </span>
              </div>
              <p className="text-xs" style={{ color: 'var(--apple-text-tertiary)' }}>
                News: BBC, CNN, NYT, Guardian, Al Jazeera, Reuters + 100 more
                <br />
                Social: Reddit, Hacker News, Mastodon, Google Trends, Twitter/X (via NimbleWay)
                <br />
                Markets: Alpha Vantage (S&P 500, NASDAQ, Dow Jones)
                <br />
                Weather: Open-Meteo (global city data)
              </p>
            </div>

            {/* Keyboard Shortcuts Hint */}
            <div className="mt-4 text-center">
              <p className="text-xs" style={{ color: 'var(--apple-text-tertiary)' }}>
                Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 font-mono">?</kbd> for keyboard shortcuts
                {' '}•{' '}
                Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 font-mono">T</kbd> for guided tour
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default HelpAboutModal;
